import { z } from 'zod';

import { KbEditProposalSchema, PromptRuleProposalSchema } from './cs-review-proposals';
import { CsDraftSchema } from './outcomes';

export const CS_REVIEWER_SLUG = 'cs-draft-reviewer';

const INTERCOM_CONVERSATION_ID = /^\d{6,24}$/;

export function intercomConversationIdFrom(input: string): string | null {
  const trimmed = input.trim();
  if (INTERCOM_CONVERSATION_ID.test(trimmed)) {
    return trimmed;
  }
  const match = /conversations?\/(\d{6,24})(?:[/?#]|$)/.exec(trimmed);
  return match?.[1] ?? null;
}

export const OpenCsReviewSchema = z.object({
  conversation: z
    .string()
    .min(1)
    .max(500)
    .refine((value) => intercomConversationIdFrom(value) !== null, {
      message: 'Paste an Intercom conversation link or id.',
    }),
  complaint: z.string().max(4000).optional(),
});
export type OpenCsReview = z.infer<typeof OpenCsReviewSchema>;

export const CsReviewSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  drafterSessionId: z.uuid().nullable(),
  intercomConversationId: z.string(),
  conversationUrl: z.string().nullable(),
  draft: CsDraftSchema.nullable(),
  articlePaths: z.array(z.string()),
  openedByEmail: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type CsReview = z.infer<typeof CsReviewSchema>;

export const CsReviewMessageSchema = z.object({
  content: z.string().min(1).max(8000),
});
export type CsReviewMessage = z.infer<typeof CsReviewMessageSchema>;

export const CS_REVIEW_DECISIONS = ['confirmed', 'approved', 'rejected'] as const;
export const CsReviewDecisionKindSchema = z.enum(CS_REVIEW_DECISIONS);

export const CsReviewDecisionSchema = z.object({
  decision: CsReviewDecisionKindSchema,
  decidedByEmail: z.string().nullable(),
  decidedAt: z.iso.datetime(),
  result: z.record(z.string(), z.unknown()),
});
export type CsReviewDecision = z.infer<typeof CsReviewDecisionSchema>;

const CsReviewProposalBaseSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  intercomConversationId: z.string(),
  messageSeq: z.number().int(),
  createdAt: z.iso.datetime(),
  decision: CsReviewDecisionSchema.nullable(),
});

export const CsReviewProposalSchema = z.discriminatedUnion('kind', [
  CsReviewProposalBaseSchema.extend({
    kind: z.literal('kb-edit-proposal'),
    payload: KbEditProposalSchema,
  }),
  CsReviewProposalBaseSchema.extend({
    kind: z.literal('prompt-rule-proposal'),
    payload: PromptRuleProposalSchema,
  }),
]);
export type CsReviewProposal = z.infer<typeof CsReviewProposalSchema>;

export const CsReviewProposalListSchema = z.array(CsReviewProposalSchema);
export type CsReviewProposalList = z.infer<typeof CsReviewProposalListSchema>;

export const ListCsReviewProposalsQuerySchema = z.object({
  sessionId: z.uuid().optional(),
  pending: z.stringbool().optional(),
});
export type ListCsReviewProposalsQuery = z.infer<typeof ListCsReviewProposalsQuerySchema>;

export const ConfirmKbProposalResultSchema = z.object({
  articleId: z.uuid(),
  articleVersion: z.number().int().positive(),
  notePosted: z.boolean(),
});
export type ConfirmKbProposalResult = z.infer<typeof ConfirmKbProposalResultSchema>;

export const ApprovePromptRuleResultSchema = z.object({
  versionId: z.uuid(),
});
export type ApprovePromptRuleResult = z.infer<typeof ApprovePromptRuleResultSchema>;

export const CsReviewScoreWeekSchema = z.object({
  weekStart: z.iso.date(),
  reviews: z.number().int().nonnegative(),
  accuracy: z.number(),
  style: z.number(),
  judgement: z.number(),
  match: z.number(),
});
export type CsReviewScoreWeek = z.infer<typeof CsReviewScoreWeekSchema>;

export const CsReviewScoreWeeksSchema = z.array(CsReviewScoreWeekSchema);
export type CsReviewScoreWeeks = z.infer<typeof CsReviewScoreWeeksSchema>;
